import { Box, Flex } from '@web/components/atoms/Layout'
import { scrollToVisibleAnchor } from '@web/components/page-components/utils'
import { Branding } from '@web/components/utils/branding/useBrandingsStore'
import { ComponentType, type Page, type PageComponent as PageComponentType } from '@web/graphql-queries/graphql'
import { PageComponent } from './PageComponent'
import Header from './PageComponent/Header'
import Footer from './PageComponent/Footer'
import { useEffect } from 'react'
import PageLayoutVariables from './PageLayoutVariables'
import { CustomScript } from './CustomScript'

type PageLayoutProps = {
  branding: Branding | null
  layout: Page | null
}

const PageLayout = ({ branding, layout, children }: React.PropsWithChildren<PageLayoutProps>) => {
  const components = new Map(layout?.components.map((c) => [c.id, c]) as [string, PageComponentType][])
  const pageOptions = layout?.components?.find((c) => c.type === ComponentType.PageOptions)

  useEffect(() => {
    const scrollFromHash = () => {
      if (window.location.hash) {
        scrollToVisibleAnchor(window.location.hash)
      }
    }

    scrollFromHash()
    window.addEventListener('hashchange', scrollFromHash)

    return () => {
      window.removeEventListener('hashchange', scrollFromHash)
    }
  }, [])

  useEffect(() => {
    if (!pageOptions?.styles?.css) {
      return
    }

    let styleEl = document.querySelector('#global-css') as HTMLStyleElement
    if (!styleEl) {
      styleEl = document.createElement('style')
      styleEl.id = 'global-css'
      document.head.appendChild(styleEl)
    }
    styleEl.textContent = pageOptions.styles.css
  }, [pageOptions?.styles?.css])

  return (
    <PageLayoutVariables branding={branding} layout={layout}>
      {pageOptions?.styles?.customScript && <CustomScript script={pageOptions.styles.customScript} />}
      <Flex
        direction="column"
        minHeight="100vh"
        width="100%"
        css={{
          maxW: pageOptions?.styles?.maxWidth ? `${pageOptions?.styles?.maxWidth}px` : '100%',
          '@media screen and (max-width: 900px)': {
            maxW: pageOptions?.mobileStyles?.maxWidth ? `${pageOptions?.mobileStyles?.maxWidth}px` : '100%',
          },
        }}
      >
        {layout?.header && <Header {...layout.header} />}
        <Box
          id="desktop-layout"
          css={{
            display: 'none',
            '@media screen and (min-width: 900px)': {
              '&': {
                display: 'block',
              },
            },
          }}
        >
          {layout?.desktop.map((c) => (
            <PageComponent key={c.id} {...c} allComponents={components} />
          ))}
        </Box>
        <Box
          id="mobile-layout"
          css={{
            display: 'block',
            '@media screen and (min-width: 900px)': {
              '&': {
                display: 'none',
              },
            },
          }}
        >
          {layout?.mobile?.map((c) => (
            <PageComponent key={c.id} {...c} allComponents={components} />
          ))}
        </Box>

        {layout?.footer && (
          <Box justifySelf="flex-end" marginTop="auto">
            <Footer {...layout.footer} />
          </Box>
        )}
        {children}
      </Flex>
    </PageLayoutVariables>
  )
}

export default PageLayout
